import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { Loader2, Trophy, Gift, RefreshCw } from 'lucide-react';

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

// Self-contained monthly verified-link prize draw, rendered by the "Review
// Draw" tab on the admin Reviews page.
export default function ReviewDrawPanel() {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const now = new Date();
  const prevMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  prevMonth.setMonth(prevMonth.getMonth() - 1);
  const [month, setMonth] = useState(prevMonth.getMonth() + 1);
  const [year, setYear] = useState(prevMonth.getFullYear());

  const runDraw = async () => {
    setRunning(true);
    setError('');
    setResult(null);
    try {
      const res = await base44.functions.invoke('adminReviewDraw', { month, year });
      if (res?.error) throw new Error(res.error);
      setResult(res);
    } catch (err) {
      setError(err.message || 'Draw failed.');
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="border border-white/10 bg-[#111115] p-6">
        <div className="flex flex-col sm:flex-row sm:items-end gap-4">
          <div>
            <label className="text-white/40 text-[10px] font-bold tracking-widest mb-2 block">MONTH</label>
            <select
              value={month}
              onChange={(e) => setMonth(Number(e.target.value))}
              className="bg-[#0d0d0f] border border-white/10 text-white text-sm font-bold h-10 px-3 focus:outline-none focus:border-primary/60"
            >
              {MONTH_NAMES.map((m, i) => (
                <option key={m} value={i + 1} className="bg-[#0d0d0f]">{m}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-white/40 text-[10px] font-bold tracking-widest mb-2 block">YEAR</label>
            <select
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              className="bg-[#0d0d0f] border border-white/10 text-white text-sm font-bold h-10 px-3 focus:outline-none focus:border-primary/60"
            >
              {[now.getFullYear(), now.getFullYear() - 1].map((y) => (
                <option key={y} value={y} className="bg-[#0d0d0f]">{y}</option>
              ))}
            </select>
          </div>
          <button
            onClick={runDraw}
            disabled={running}
            className="bg-primary hover:bg-primary/90 text-white font-bold text-xs tracking-widest px-6 py-3 transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} RUN DRAW
          </button>
        </div>
      </div>

      {error && (
        <div className="border border-red-500/20 bg-red-500/5 text-red-400 text-xs px-4 py-3">{error}</div>
      )}

      {result && (
        <div className="border border-white/10 bg-[#111115] p-6">
          <p className="text-white/40 text-[10px] font-bold tracking-widest mb-1">RESULT — {result.month}</p>
          {result.winner ? (
            <>
              <div className="flex items-center gap-3 mt-4 mb-4">
                <div className="w-12 h-12 bg-primary/20 border border-primary/40 flex items-center justify-center">
                  <Trophy className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <p className="text-white font-black text-xl uppercase">{result.winner.author_name || 'Anonymous'}</p>
                  <p className="text-white/40 text-xs">{result.winner.customer_email || 'No email'}</p>
                </div>
              </div>
              <div className="space-y-1 text-xs text-white/50">
                <p>Category: <span className="text-white/80 font-bold uppercase">{(result.winner.review_category || 'products').replace('_', ' ')}</span></p>
                <p>Rating: <span className="text-white/80 font-bold">{result.winner.rating}/5</span></p>
                <p>Entrants: <span className="text-white/80 font-bold">{result.entrants}</span></p>
                {result.winner.source_url && (
                  <a href={result.winner.source_url} target="_blank" rel="noopener noreferrer" className="inline-block text-primary hover:text-primary/80 break-all">
                    View original review →
                  </a>
                )}
              </div>
            </>
          ) : (
            <div className="flex items-center gap-3 mt-4">
              <Gift className="w-5 h-5 text-white/30" />
              <p className="text-white/40 text-sm">No verified-link reviews were submitted in {MONTH_NAMES[month - 1]} {year}. Nothing to draw.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}